/* eslint-disable eqeqeq */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  FeatureGroup
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "mapbox-gl/dist/mapbox-gl.css";
import "mapbox-gl-leaflet";
import L from "leaflet";

import { useTranslate } from "../hooks/LanguageProvider";
import IconMarker from "../components/Map/Icon";
import { Col, Row } from "react-bootstrap";
import "../components/Map/map.css";
import VehicleWidget from "../components/Map/vehicleWidget";
import MarkerClusterGroup from "react-leaflet-cluster";
import Slider from "react-slick";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { engineStat, engineStatClass } from "../utilities/functions";
import { useSpring, animated } from "react-spring";

import ItineraryLayerData from "../components/Map/ItineraryLayer";
import RouteSimulator from "../components/Map/RouteSimulator"; // ⬅️ simulateur réactivé
import { Bounce, toast } from "react-toastify";
import PrintControl from "../components/Map/PrintControl";

import FullscreenControlCustom from "../components/Map/FullscreenControlCustom";
import ZoomControl from "../components/ui/ZoomControl";


const backendUrl = process.env.REACT_APP_BACKEND_URL;

/* ------------------------------------------------------------
   Types
------------------------------------------------------------ */
type Vehicle = {
  id_vehicule: number;
  immatriculation_vehicule: string;
  LAT: number;
  LON: number;
  SOG: number;
  COG: number;
  ENGINESTAT: number;
  TIMESTAMP: string;
  GPSDIST: number;
  PSN: string;
  nom_conducteur: string;
  nom_groupe: string;
  prenom_conducteur: string;
};

type toChartData = { x: number; y: number };

/* ------------------------------------------------------------
   Utils
------------------------------------------------------------ */
function ToUTCTime(isoDateString: string): number {
  if (!isoDateString) return 0;
  const dateComponents = isoDateString.split(/[-T:.Z]/).map((v) => parseInt(v, 10));
  return Date.UTC(
    dateComponents[0],
    dateComponents[1] - 1,
    dateComponents[2],
    dateComponents[3],
    dateComponents[4],
    dateComponents[5],
    0
  );
}

const pointTs = (p: any): number | null => {
  const raw = p?.end ?? p?.start ?? p?.TIMESTAMP_END ?? p?.TIMESTAMP ?? null;
  if (!raw) return null;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : null;
};

const samePoint = (a: any, b: any) =>
  a && b &&
  a.LAT === b.LAT &&
  (a.LON ?? a.LNG) === (b.LON ?? b.LNG) &&
  pointTs(a) === pointTs(b);

/* ------------------------------------------------------------
   Hook: récupérer la map
------------------------------------------------------------ */
const MapEventHandler: React.FC<{ onMapReady: (mapInstance: any) => void }> = ({ onMapReady }) => {
  const map = useMap();
  useEffect(() => { if (map) onMapReady(map); }, [map, onMapReady]);
  return null;
};

/* ------------------------------------------------------------
   Composant principal
------------------------------------------------------------ */
const Map: React.FC = () => {
  const center: [number, number] = [30.5670197, 4.6911217];
  const zoom = 5;
  const MaxZoom = 18;
  const MinZoom = 5;
  const [printBounds, setPrintBounds] = useState<L.LatLngBoundsExpression | null>(null);
  const { translate } = useTranslate();
  const user_id = localStorage.getItem("userid");

  // --------- États data / UI ---------
  const [list_markers, setMarkers] = useState<Vehicle[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [baseMap, setBaseMap] = useState<string>("google_roadmap");
  const [listLayout, setlistLayout] = useState("layoutfloat");
  const [mapLayout, setMapLayout] = useState("col-12");
  const [vehicleDetail, setVehicleDetail] = useState<string>("none");
  const [selectedItem, setSelectedItem] = useState<Vehicle | null>(null);

  const [DistanceSpeedData, setDistanceSpeed] = useState<any>([]);
  const [TemperaturetData, setTemperature] = useState<any>([]);
  const [FuelTankData, setFuelTank] = useState<any>([]);

  // Itinéraire
  const [itineraryPoints, setItineraryPoints] = useState<any[]>([]);
  const [itineraryVisible, setItineraryVisible] = useState(false);
  const [activePSN, setActivePSN] = useState<string | null>(null);
  const [vehiclesVisible, setVehiclesVisible] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [itineraryNotice, setItineraryNotice] = useState<string | null>(null);
  const [followLive, setFollowLive] = useState<boolean>(true);
  const [autoFitItinerary, setAutoFitItinerary] = useState<boolean>(true);
  const lastTsRef = useRef<number | null>(null);

  // Graphiques / slider
  const [activeChart, setActiveChart] = useState<'distance' | 'fuel' | 'temp' | null>(null);
  const [chartPSN, setChartPSN] = useState<string | null>(null);

  // layout & filtres
  const [clicked, setClicked] = useState(false);
  const [slideState, setSlideState] = useState<string>("slide-state");
  const [ungroupMarker, setUngroupMarker] = useState<boolean>(true);
  const [Driving, setDriving] = useState(0);
  const [Parking, setParking] = useState(0);
  const [ParkingEngineRunning, setParkingEngineRunning] = useState(0);
  const [LastTransmission, setLastTransmission] = useState(0);
  const [vehicleState, setVehicleState] = useState<string | null>(null);

  const sliderRef = useRef<Slider | null>(null);
  const mapRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Animation UI liste
  const animationProps = useSpring({
    transform: clicked ? "scale(1.1)" : "scale(1)",
    overflow: "hidden",
    marginTop: clicked ? "55px" : "10px",
  });

  // GroupComponent (cluster ou non)
  const GroupComponent: React.ComponentType<any> = ungroupMarker ? MarkerClusterGroup : FeatureGroup;

  // Slider charts
  const settings = { dots: false, infinite: true, speed: 500, slidesToShow: 1, slidesToScroll: 1, variableHeight: true };
  const goToSlide = (index: number) => sliderRef.current?.slickGoTo(index);

  // Base maps
  const baseMapOptions = [
    { value: "google_roadmap", label: "Google Roadmap", icon: "https://mt0.google.com/vt/lyrs=m&hl=en&x={x}&y={y}&z={z}" },
    { value: "osm", label: "OSM", icon: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" },
    { value: "hot", label: "OSM HOT", icon: "https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png" },
    { value: "dark", label: "DARK", icon: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png" },
    { value: "cycle", label: "CYCLE MAP", icon: "https://dev.{s}.tile.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png" },
    { value: "google_traffic", label: "Google Traffic", icon: "https://{s}.google.com/vt/lyrs=m@221097413,traffic&x={x}&y={y}&z={z}" },
    { value: "google_satellite", label: "Google Satellite", icon: "https://www.google.com/maps/vt?lyrs=s@189&gl=us&x={x}&y={y}&z={z}" },
    { value: "google_terrain", label: "Google Terrain", icon: "https://www.google.com/maps/vt?lyrs=p@189&gl=us&x={x}&y={y}&z={z}" },
  ];
  const basemapsDict: Record<string, string> = Object.fromEntries(baseMapOptions.map((o) => [o.value, o.icon]));

  const toolbarStyle: React.CSSProperties = {

    top: 10,
    zIndex: 1000,
    background: "#fff",
    padding: "4px 8px",
    borderRadius: 4,
    boxShadow: "0 1px 4px rgba(0,0,0,.3)",
    display: "flex",
    gap: 6,
    alignItems: "center",

  };

  // Divers
  const handleWheelScroll = (event: React.WheelEvent<HTMLDivElement>) => {
    event.stopPropagation();
    const container = containerRef.current;
    if (container) container.scrollTop += event.deltaY;
  };
  const mapLayoutClick = () => {
    setlistLayout((s) => (s === "layoutfloat" ? "layoutleft" : "layoutfloat"));
    setMapLayout((s) => (s === "col-12" ? "col-9" : "col-12"));
  };
  const handleMarker = (state: string) => setVehicleState((curr) => (curr === state ? null : state));

  const timeZone = (datetime: string) => {
    const d = new Date(datetime);
    d.setHours(d.getHours() + 1);
    return d.toLocaleString();
  };
  const timeZoneDST = (datetime: string) => {
    const d = new Date(datetime);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const h = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    const s = String(d.getSeconds()).padStart(2, "0");
    return `${y}-${m}-${day} ${h}:${min}:${s}`;
  };

  // ------------------------------------------------------------
  // API: getReportByType
  // ------------------------------------------------------------
  const getReportByType = async (type: number, PSN: string, slide: number) => {
    const res = await fetch(`${backendUrl}/api/map/reports/${type}/${PSN}`, { mode: "cors" });
    if (!res.ok) throw new Error("Erreur lors de la récupération du véhicule");
    if (slide != -1) goToSlide(slide);
    const data = await res.json();

    if (type == 1) {
      return data?.data ?? [];
    }

    if (type == 4) {
      setSlideState("");
      const arr = data.data;
      let previousGPSDIST = arr[0].GPSDIST / 1000;
      let GPSDISTs = 0;

      const Distance: toChartData[] = arr.map((item: any) => {
        const currentGPSDIST = item.GPSDIST / 1000;
        const diff = currentGPSDIST - previousGPSDIST;
        previousGPSDIST = currentGPSDIST;
        GPSDISTs += diff;
        return { x: ToUTCTime(item.TIMESTAMP), y: Math.round(GPSDISTs) };
      });

      const Speed: toChartData[] = arr.map((item: any) => ({
        x: ToUTCTime(item.TIMESTAMP),
        y: parseFloat(item.SOG),
      }));
      const EngineStat: toChartData[] = arr.map((item: any) => ({
        x: ToUTCTime(item.TIMESTAMP),
        y: parseFloat(item.ENGINESTAT),
      }));

      setDistanceSpeed({
        chart: { zoomType: "x", height: 120, width: null },
        title: { text: "", align: "center" },
        subtitle: { text: "", align: "center" },
        xAxis: [{ gridLineWidth: 1, type: "datetime", crosshair: true }],
        yAxis: [
          {
            labels: { format: "{value} Km", style: { color: "#007bff" } },
            title: { text: translate("Distance"), style: { color: "#007bff" } },
            opposite: true,
          },
          {
            gridLineWidth: 0,
            title: { text: translate("Speed"), style: { color: "#41b346" } },
            labels: { format: "{value} Km/h", style: { color: "#41b346" } },
          },
          {
            gridLineWidth: 0,
            title: { text: translate("Contact"), style: { color: "#fd1e0d" } },
            labels: { format: "{value} ", style: { color: "#fd1e0d" } },
          },
        ],
        tooltip: { shared: true },
        legend: { layout: "horizontal", align: "center", x: 0, verticalAlign: "bottom", y: 21, floating: true },
        plotOptions: { series: { turboThreshold: Distance.length } },
        series: [
          { name: translate("Distance"), type: "spline", yAxis: 0, data: Distance, tooltip: { valueSuffix: " Km" } },
          { name: translate("Speed"), type: "spline", color: "#41b346", yAxis: 1, data: Speed, tooltip: { valueSuffix: " km/h" } },
          { name: translate("Contact"), type: "line", color: "#fd1e0d", yAxis: 2, data: EngineStat, tooltip: { valueSuffix: " " } },
        ],
      });
    }

    if (type == 8) {
      const arr = data.data;
      setSlideState("");

      const FIRST_FUELLVU = arr[0].FUELLVU;

      const TFUEL: toChartData[] = arr.map((item: any) => ({
        x: ToUTCTime(item.TIMESTAMP),
        y: parseFloat((item.FUELLVU - FIRST_FUELLVU).toFixed(2)),
      }));

      let previousVEHDIST = arr[0].VEHDIST / 1000;
      let VEHDISTs = 0;
      const VEHDIST: toChartData[] = arr.map((item: any) => {
        const current = item.VEHDIST / 1000;
        const diff = current - previousVEHDIST;
        previousVEHDIST = current;
        VEHDISTs += diff;
        return { x: ToUTCTime(item.TIMESTAMP), y: Math.round(VEHDISTs) };
      });

      const FUELLVL: toChartData[] = arr.map((item: any) => ({
        x: ToUTCTime(item.TIMESTAMP),
        y: Math.round(item.FUELLVL),
      }));

      setFuelTank({
        chart: { zoomType: "x", height: 120 },
        title: { text: "", align: "center" },
        subtitle: { text: translate(""), align: "center" },
        xAxis: [{ gridLineWidth: 1, type: "datetime", crosshair: true }],
        yAxis: [
          {
            gridLineWidth: 0,
            title: { text: translate("Distance"), style: { color: "#007bff" } },
            labels: { format: "{value} Km", style: { color: "#007bff" } },
          },
          {
            labels: { format: "{value} %", style: { color: "#fd1e0d" } },
            title: { text: translate("Tank level"), style: { color: "#fd1e0d" } },
            opposite: true,
          },
          {
            gridLineWidth: 0,
            title: { text: translate("Fuel consumption"), style: { color: "#0080FF" } },
            labels: { format: "{value} L", style: { color: "#0080FF" } },
            opposite: true,
          },
        ],
        tooltip: { shared: true },
        legend: { layout: "horizontal", align: "center", x: 0, verticalAlign: "bottom", y: 21, floating: true },
        plotOptions: { series: { turboThreshold: VEHDIST.length } },
        series: [
          { name: translate("Distance"), type: "spline", yAxis: 0, data: VEHDIST, tooltip: { valueSuffix: " Km" } },
          { name: translate("Fuel consumption"), type: "spline", color: "#41b346", yAxis: 1, data: TFUEL, tooltip: { valueSuffix: " L" } },
          { name: translate("Tank level"), type: "line", color: "#fd1e0d", yAxis: 2, data: FUELLVL, tooltip: { valueSuffix: " %" } },
        ],
      });
    }

    if (type == 19) {
      const arr = data.data;
      setSlideState("");

      const AVGHUM: toChartData[] = arr.map((item: any) => ({
        x: ToUTCTime(item.TIMESTAMP),
        y: parseFloat(item.AVGHUM),
      }));
      const AVGTEMP: toChartData[] = arr.map((item: any) => ({
        x: ToUTCTime(item.TIMESTAMP),
        y: parseFloat(item.AVGTEMP) / 1000,
      }));

      setTemperature({
        chart: { zoomType: "x", height: 120 },
        title: { text: translate(""), align: "center" },
        subtitle: { text: translate("Data values are averaged per period. Zoom to view more data"), align: "center" },
        xAxis: [{ gridLineWidth: 1, type: "datetime", crosshair: true }],
        yAxis: [
          {
            labels: { format: "{value} C°", style: { color: "#fd1e0d" } },
            title: { text: translate("Temperature"), style: { color: "#fd1e0d" } },
            opposite: true,
          },
          {
            gridLineWidth: 0,
            title: { text: translate("Humidity"), style: { color: "#0080FF" } },
            labels: { format: "{value} %", style: { color: "#0080FF" } },
          },
        ],
        tooltip: { shared: true },
        legend: { layout: "horizontal", align: "center", x: 0, verticalAlign: "bottom", y: 21, floating: true },
        plotOptions: { series: { turboThreshold: AVGTEMP.length } },
        series: [
          { name: translate("Temperature"), type: "spline", yAxis: 0, data: AVGTEMP, tooltip: { valueSuffix: " C°" } },
          { name: translate("Humidity"), type: "spline", color: "#41b346", yAxis: 1, data: AVGHUM, tooltip: { valueSuffix: " %" } },
        ],
      });
    }

    return null;
  };

  // ------------------------------------------------------------
  // Récupération des véhicules
  // ------------------------------------------------------------

  const isFirstLoadRef = useRef(true);
  const BATCH_SIZE = 500;

  useEffect(() => {
    const getMarkers = async () => {
      try {
        const response = await fetch(
          `${backendUrl}/api/map/find/${user_id}`,
          { mode: "cors" }
        );
        if (!response.ok) throw new Error("Failed to fetch vehicle data");

        const data: Vehicle[] = await response.json();

        // ==================================================
        // CALCULS MÉTIER (INCHANGÉS)
        // ==================================================
        const starting = data.filter(v => v.ENGINESTAT === 1 && v.SOG > 5);
        const parking = data.filter(v => v.ENGINESTAT === 0);
        const running = data.filter(v => v.ENGINESTAT === 1 && v.SOG < 5);
        const filtered = data.filter(v => {
          const hours =
            (Date.now() - new Date(timeZone(v.TIMESTAMP)).getTime()) / 3600000;
          return hours > 1;
        });

        setDriving(starting.length);
        setParking(parking.length);
        setParkingEngineRunning(running.length);
        setLastTransmission(filtered.length);

        // ==================================================
        // AFFICHAGE (SIMPLE & CLAIR)
        // ==================================================

        // 🔹 CAS 1 : PREMIER CHARGEMENT → DÉCOUPAGE
        if (isFirstLoadRef.current) {
          setMarkers(data.slice(0, BATCH_SIZE));

          let index = BATCH_SIZE;
          const id = setInterval(() => {
            setMarkers(prev => {
              const next = data.slice(index, index + BATCH_SIZE);
              index += BATCH_SIZE;

              if (!next.length) {
                clearInterval(id);
                isFirstLoadRef.current = false; // 🔑 fini le premier chargement
                return prev;
              }

              return prev.concat(next);
            });
          }, 200);

          return;
        }

        // 🔹 CAS 2 : AUTRES CHARGEMENTS → DIRECT
        setMarkers(data);

      } catch (err) {
        console.error("Error fetching vehicle data:", err);
      }
    };

    getMarkers();
    const refreshInterval = setInterval(getMarkers, 6000);
    return () => clearInterval(refreshInterval);
  }, [user_id]);

  // ------------------------------------------------------------
  // Sélection véhicule / recentrage
  // ------------------------------------------------------------
  const [userInteraction, setUserInteraction] = useState(false);
  const flyToVehicle = useCallback((map: any, vehicle: Vehicle) => {
    if (vehicle && !userInteraction) map.flyTo([vehicle.LAT, vehicle.LON], 15, { duration: 1 });
  }, [userInteraction]);

  useEffect(() => {
    const map = mapRef.current;
    if (selectedItem && map) flyToVehicle(map, selectedItem);
  }, [selectedItem, flyToVehicle]);

  const handleItemClick = (item: Vehicle) => {
    if (!mapRef.current) return;
    setVehicleDetail("flex");
    setSearchTerm(item.immatriculation_vehicule);
    setSelectedItem(item);
    setUserInteraction(false);
    mapRef.current.flyTo([item.LAT, item.LON], 15, { duration: 1 });
    setClicked(true);
  };

  // ------------------------------------------------------------
  // Handlers sliders (TOGGLE)
  // ------------------------------------------------------------
  const handleOnCloseWidget = () => {
    setVehicleDetail("none");
    setSearchTerm("");
    setSlideState("slide-state");
    setClicked(false);
  };

  const handleDistanceSpeedClick = (PSN: string) => {
    if (activeChart === 'distance' && chartPSN === PSN && slideState === '') {
      setSlideState('slide-state'); setActiveChart(null); setChartPSN(null); return;
    }
    setActiveChart('distance'); setChartPSN(PSN); setSlideState('');
    getReportByType(4, PSN, 0); if (typeof goToSlide === 'function') goToSlide(0);
  };

  const handleFuelTankClick = (PSN: string) => {
    if (activeChart === 'fuel' && chartPSN === PSN && slideState === '') {
      setSlideState('slide-state'); setActiveChart(null); setChartPSN(null); return;
    }
    setActiveChart('fuel'); setChartPSN(PSN); setSlideState('');
    getReportByType(8, PSN, 1); if (typeof goToSlide === 'function') goToSlide(1);
  };

  const handleTemperatureDiagramClick = (PSN: string) => {
    if (activeChart === 'temp' && chartPSN === PSN && slideState === '') {
      setSlideState('slide-state'); setActiveChart(null); setChartPSN(null); return;
    }
    setActiveChart('temp'); setChartPSN(PSN); setSlideState('');
    getReportByType(19, PSN, 2); if (typeof goToSlide === 'function') goToSlide(2);
  };

  // ------------------------------------------------------------
  // Itinéraire : toggle + vide + live append 15s
  // ------------------------------------------------------------
  const handleItineraryClick = useCallback(async (PSN: string) => {
    // Toggle OFF si déjà en R.I. sur ce PSN
    if (itineraryVisible && activePSN === PSN) {
      setItineraryVisible(false);
      setItineraryPoints([]);
      setActivePSN(null);
      setVehiclesVisible(true);
      setItineraryNotice(null);
      lastTsRef.current = null;

      toast.info(translate("Itinerary hidden"), {
        position: "bottom-right",
        autoClose: 2400,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
      });
      return;
    }

    // Toast de chargement
    const loadingId = toast.loading(translate("Loading itinerary…"), {
      position: "bottom-right",
      // on laisse sans autoClose pour un vrai “loading”
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: false,
      theme: "light",
      transition: Bounce,
    });

    try {
      setLoading(true);
      setErr(null);
      setItineraryNotice(null);
      setActivePSN(PSN);

      const raw = await getReportByType(1, PSN, -1);
      const points = Array.isArray(raw) ? raw : (raw?.repportDatas ?? raw?.data ?? []);

      // Cas vide → toast.warn
      if (!points || points.length === 0) {
        setItineraryVisible(false);
        setItineraryPoints([]);
        setActivePSN(null);
        setVehiclesVisible(true);
        setItineraryNotice(null);
        lastTsRef.current = null;

        toast.update(loadingId, {
          render: translate("No itinerary data for this vehicle / period."),
          type: "warning",
          isLoading: false,
          autoClose: 2400,
          closeOnClick: true,
          pauseOnHover: true,
        });
        return;
      }

      // OK → afficher tracé + toast success
      setItineraryPoints(points);
      setItineraryVisible(true);
      setVehiclesVisible(false);
      setAutoFitItinerary(true);
      lastTsRef.current = pointTs(points[points.length - 1]);

      toast.update(loadingId, {
        render: translate("Itinerary loaded"),
        type: "success",
        isLoading: false,
        autoClose: 1600,
        closeOnClick: true,
        pauseOnHover: true,
      });
    } catch (e: any) {
      // Erreur → toast.error
      setErr(e?.message ?? translate("Error while loading itinerary"));
      setItineraryPoints([]);
      setItineraryVisible(false);
      setVehiclesVisible(true);
      setItineraryNotice(null);
      lastTsRef.current = null;

      toast.update(loadingId, {
        render: e?.message ?? translate("Error while loading itinerary"),
        type: "error",
        isLoading: false,
        autoClose: 3200,
        closeOnClick: true,
        pauseOnHover: true,
      });
    } finally {
      setLoading(false);
    }
  }, [itineraryVisible, activePSN, translate]);

  // Désactiver l'autoFit après 1er rendu
  useEffect(() => {
    if (itineraryVisible && autoFitItinerary && itineraryPoints.length) {
      const t = setTimeout(() => setAutoFitItinerary(false), 0);
      return () => clearTimeout(t);
    }
  }, [itineraryVisible, autoFitItinerary, itineraryPoints.length]);

  // Live append 15s
  useEffect(() => {
    if (!itineraryVisible || !activePSN) return;

    const id = setInterval(async () => {
      try {
        const raw = await getReportByType(1, activePSN, -1);
        const incoming = Array.isArray(raw) ? raw : (raw?.repportDatas ?? raw?.data ?? []);
        if (!incoming || incoming.length === 0) return;

        const apiLast = pointTs(incoming[incoming.length - 1]);

        if (lastTsRef.current == null) {
          setItineraryPoints(incoming);
          lastTsRef.current = apiLast;
          return;
        }

        const news = incoming.filter((p: any) => {
          const ts = pointTs(p);
          return ts !== null && ts > (lastTsRef.current as number);
        });

        if (news.length) {
          setItineraryPoints((prev) => {
            const merged = [...prev, ...news];
            if (merged.length >= 2 && samePoint(merged[merged.length - 1], merged[merged.length - 2])) {
              merged.splice(merged.length - 1, 1);
            }
            return merged;
          });
          lastTsRef.current = apiLast ?? lastTsRef.current;

          if (followLive && mapRef.current) {
            const last = news[news.length - 1];
            const lng = (last.LON ?? last.LNG);
            if (typeof lng === "number") mapRef.current.panTo([last.LAT, lng]);
          }
        }
      } catch {
        // silencieux
      }
    }, 15000);

    return () => clearInterval(id);
  }, [itineraryVisible, activePSN, followLive]);

  // ------------------------------------------------------------
  // Filtres / liste
  // ------------------------------------------------------------
  const filteredItems = list_markers.filter((item) =>
    item.immatriculation_vehicule.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.nom_conducteur && item.nom_conducteur.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.prenom_conducteur && item.prenom_conducteur.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setVehicleDetail("none");
  };

  // Path pour simulateur
  const simPath = useMemo<[number, number][]>(() =>
    (itineraryPoints ?? [])
      .filter((p: any) => typeof p?.LAT === "number" && (typeof p?.LON === "number" || typeof p?.LNG === "number"))
      .map((p: any) => [p.LAT, (p.LON ?? p.LNG) as number])
    , [itineraryPoints]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  const [isPrinting, setIsPrinting] = useState(false);

  const periodLabel = /* ex: "01/09/2025 08:00 → 01/09/2025 18:00" */ "";
  const onBeforePrint = useCallback(() => {
    document.body.classList.add("print-map");
    setIsPrinting(true);
  }, []);
  const onAfterPrint = useCallback(() => {
    setIsPrinting(false);
    document.body.classList.remove("print-map");
  }, []);

  const printHeaderText = useMemo(() => {
    const now = new Date().toLocaleString();
    return `R.I. ${activePSN ? `— PSN: ${activePSN} ` : ""}${periodLabel ? `— ${periodLabel} ` : ""}— ${now}`;
  }, [activePSN, periodLabel]);






  return (
    <>
      <Row>
        {/* Liste véhicules */}
        <div className={`${listLayout} col-12 col-md-3`} style={{ zIndex: 9999, paddingLeft: "30px", margin: " 20px 0" }}>
          <div style={{ margin: "0px 0 15px 0" }}>
            <button
              type="button"
              className={`btn btn-default btn-sm pull-left act ${vehicleState !== "move" ? "active" : ""}`}
              id="act_moving" onClick={() => handleMarker("move")}
              title={translate("Engine running")}
            >
              <span>{Driving} </span>
              <i className="la la-play-circle" style={{ color: "green", fontSize: "20px" }} />
            </button>
            <button
              type="button"
              className={`btn btn-delault btn-sm pull-left act ${vehicleState !== "pause" ? "active" : ""}`}
              id="act_pause" onClick={() => handleMarker("pause")}
              title={translate("Parking with engine started")}
            >
              <span>{ParkingEngineRunning} </span>
              <i className="la la-pause-circle" style={{ color: "red", fontSize: "20px" }} />
            </button>
            <button
              type="button"
              className={`btn btn-delault btn-sm pull-left act ${vehicleState !== "stop" ? "active" : ""}`}
              id="act_stoping" onClick={() => handleMarker("stop")}
              title={translate("Parking")}
            >
              <span>{Parking} </span>
              <i className="las la-stop-circle" style={{ color: "blue", fontSize: "20px" }} />
            </button>
            <button
              type="button"
              className={`btn btn-delault btn-sm pull-left act ${vehicleState !== "absence" ? "active" : ""}`}
              id="act_absence" onClick={() => handleMarker("absence")}
              title={translate("Last transmission")}
            >
              <span>{LastTransmission} </span>
              <i className="la la-history" style={{ fontSize: "20px" }} />
            </button>
            <button
              type="button"
              className="btn btn-delault btn-sm pull-left act"
              id="act_regrouper" onClick={() => setUngroupMarker((v) => !v)}
              title={translate("Group or ungroup vehicles")}
            >
              <i className="la la-object-ungroup" style={{ fontSize: "20px" }} />
            </button>
          </div>

          <span className="map-search">
            <input
              type="text"
              placeholder={translate("Search vehicle, driver...")}
              value={searchTerm}
              onChange={handleSearchChange}
            />
            ({list_markers.length}) <i className="las la-search"></i>
          </span>

          <span className="map-layout" onClick={mapLayoutClick}>
            <i className="las la-list" title={translate("Extend")} style={{ fontSize: "24px" }} />
          </span>

          <animated.div
            className="map-list"
            ref={containerRef}
            onWheel={handleWheelScroll}
            tabIndex={0}
            style={animationProps}
          >
            {filteredItems.map((item) => {
              // flags actifs pour CE véhicule (pour styler les 4 boutons)
              const isRIActive = itineraryVisible && activePSN === item.PSN;
              const isDistanceActive = activeChart === 'distance' && chartPSN === item.PSN && slideState === '';
              const isFuelActive = activeChart === 'fuel' && chartPSN === item.PSN && slideState === '';
              const isTempActive = activeChart === 'temp' && chartPSN === item.PSN && slideState === '';

              return (
                (vehicleState === null || vehicleState === engineStatClass(item.ENGINESTAT, item.SOG)) && (
                  <div
                    key={item.id_vehicule}
                    id={item.id_vehicule.toString()}
                    onClick={(e) => { e.stopPropagation(); handleItemClick(item); }}
                    className={`VehicleWidget ${engineStatClass(item.ENGINESTAT, item.SOG)}`}
                  >
                    <VehicleWidget
                      id={item.id_vehicule}
                      matriculation={item.immatriculation_vehicule}
                      timestamp={timeZoneDST(item.TIMESTAMP)}
                      SOG={item.SOG}
                      LAT={item.LAT}
                      LON={item.LON}
                      COG={item.COG}
                      enginestat={item.ENGINESTAT}
                      gpsdist={item.GPSDIST}
                      detail={vehicleDetail}
                      PSN={item.PSN}
                      nom_conducteur={item.nom_conducteur}
                      nom_groupe={item.nom_groupe}
                      prenom_conducteur={item.prenom_conducteur}
                      handleItineraryClick={handleItineraryClick}
                      handleDistanceSpeedClick={handleDistanceSpeedClick}
                      handleFuelTankClick={handleFuelTankClick}
                      handleTemperatureDiagramClick={handleTemperatureDiagramClick}
                      onClose={handleOnCloseWidget}
                      isRIActive={isRIActive}
                      isDistanceActive={isDistanceActive}
                      isFuelActive={isFuelActive}
                      isTempActive={isTempActive}
                    />
                  </div>
                )
              );
            })}
          </animated.div>
        </div>

        {/* Carte */}
        <div className={mapLayout} style={{ padding: 0, position: "relative" }}>


          {/* Boutons R.I. */}
          {itineraryVisible && (
            <div
              className="ri-toolbar"
              style={{ position: "absolute", zIndex: 1200, top: 10, right: 600, display: "flex", gap: 8 }}
            >
              {/* Toggle Follow */}
              <button
                type="button"
                style={{
                  ...toolbarStyle,
                  opacity: 0.9,
                  background: '#fff', // facultatif si tu veux voir l'opacité
                  padding: '7px',
                  borderRadius: '8px',
                  border: "1px solid #ddd"
                }}
                className={`icon-btn ${followLive ? "is-on" : "is-off"}`}
                onClick={() => setFollowLive(v => !v)}
                title={followLive ? translate("Stop following") : translate("Follow in real time")}
                aria-pressed={followLive}
              >
                {/* icône ciblage */}
                <i className={`las la-crosshairs ${followLive ? "orange" : "grey"}`} style={{ fontSize: "22px" }} />
                {followLive ? translate("Stop following") : translate("Follow in real time")}
              </button>

              {/* Quitter R.I. */}
              <button
                type="button"
                className="icon-btn danger"
                style={{
                  ...toolbarStyle,
                  opacity: 0.9,
                  background: '#fff', // facultatif si tu veux voir l'opacité
                  padding: '7px',
                  borderRadius: '8px',
                  border: "1px solid #ddd"
                }}
                onClick={() => {
                  setItineraryVisible(false);
                  setItineraryPoints([]);
                  setActivePSN(null);
                  setVehiclesVisible(true);
                  setItineraryNotice(null);
                  lastTsRef.current = null;
                }}
                title={translate("Exit Itinerary reconstitution")}
              >
                {/* icône sortie */}
                <i className="las la-sign-out-alt" style={{ fontSize: 22 }} /> Exit R.I.
              </button>
            </div>
          )}


          <MapContainer
            center={center}
            zoom={zoom}
            minZoom={MinZoom}
            maxZoom={MaxZoom}
            zoomControl={false}
            style={{ padding: 0, margin: 0, height: "86vh", width: "100%", zIndex: 1 }}
          >
            <MapEventHandler onMapReady={(mapInstance) => { mapRef.current = mapInstance; }} />

            <TileLayer
              attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
              url={basemapsDict[baseMap]}
            />

            {/* select basemap */}
            <div className="basemaps-container" style={{ top: 165, position: "absolute" }}>
              <select
                value={baseMap}
                onChange={(e) => setBaseMap(e.target.value)}
                style={{
                  backgroundImage: `url(${process.env.PUBLIC_URL}/asset/images/mapicon/layers.png)`,
                  appearance: "none",
                  backgroundSize: "22px",
                  backgroundPosition: " center center",
                  backgroundRepeat: "no-repeat",
                  border: "none",
                  color: "transparent",
                  outline: "none",
                }}
              >
                {baseMapOptions.map((option) => (
                  <option key={option.value} value={option.value} style={{ color: "black" }}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* select basemap */}
            <div className="basemaps-container" style={{ top: 165, position: "absolute" }}>
              <select
                value={baseMap}
                onChange={(e) => setBaseMap(e.target.value)}
                style={{
                  backgroundImage: `url(${process.env.PUBLIC_URL}/asset/images/mapicon/layers.png)`,
                  appearance: "none",
                  backgroundSize: "22px",
                  backgroundPosition: " center center",
                  backgroundRepeat: "no-repeat",
                  border: "none",
                  color: "transparent",
                  outline: "none",
                }}
              >
                {baseMapOptions.map((option) => (
                  <option key={option.value} value={option.value} style={{ color: "black" }}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <ZoomControl position="topright" />
            <FullscreenControlCustom position="topright" />


            {vehiclesVisible && (
              <GroupComponent>
                {list_markers.map((item) => (
                  <Marker
                    key={item.id_vehicule}
                    position={[item.LAT, item.LON]}
                    icon={IconMarker(item.COG, "asset/images/mapicon/car.svg", () => handleItemClick(item))}
                  >
                    <Popup>
                      <span onClick={() => handleItemClick(item)}>
                        <img
                          src={engineStat(item.ENGINESTAT, item.COG, translate).iconState}
                          alt=""
                        />{" "}
                        {item.immatriculation_vehicule}
                      </span>
                    </Popup>
                  </Marker>
                ))}
              </GroupComponent>
            )}

            {/* Itinéraire (polyline + marqueurs) */}
            <ItineraryLayerData
              points={itineraryPoints}
              visible={itineraryVisible}
              color="#0ea5e9"
              autoFit={autoFitItinerary}
            />


            {/* Simulateur (marqueur animé) */}
            {itineraryVisible && simPath.length >= 2 && (
              <RouteSimulator
                path={simPath}
                speed={50}       // km/h
                autofit={false}  // l’autoFit est géré par ItineraryLayerData au 1er rendu
                right={true}
              />
            )}

            <PrintControl
              position="topright"
              orientation="landscape"
              pageSize="A4"
              fitOnPrint="selection"       // ← imprime la dernière zone sélectionnée
              selectionEnabled={true}      // ← affiche le bouton de sélection
            // extraPrintCss=".leaflet-control-container{display:none!important;}" // optionnel
            />

          </MapContainer>
        </div>

        {/* Slider charts */}
        <div
          className="layoutfloat"
          style={{
            zIndex: 9999,
            background: " #fff",
            bottom: "0px",
            border: " 1px solid #ddd; ",
            padding: "0",
          }}
        >
          <Slider ref={sliderRef} {...settings} className={`${slideState}`}>
            {/* Distance */}
            <div className="dvc">
              <i
                className="las la-times"
                style={{ position: "absolute", zIndex: 9999, border: "1px solid #ddd", borderRadius: "19px", padding: "6px" }}
                onClick={() => { setSlideState("slide-state"); setActiveChart(null); setChartPSN(null); }}
              />
              <Row>
                <Col xs={12}>
                  <h5 style={{ textAlign: "center" }}>
                    {translate("Diagram of distance, speed and contact")}
                  </h5>
                  {DistanceSpeedData && Object.keys(DistanceSpeedData).length > 0 ? (
                    <div style={{ backgroundColor: "#fff", padding: "0", margin: "8" }}>
                      <HighchartsReact highcharts={Highcharts} options={DistanceSpeedData} />
                    </div>
                  ) : (
                    <div style={{ backgroundColor: "#fff", padding: "16px", margin: "8px", textAlign: "center" }}>
                      {translate("No data to display No data to display for distance, speed and contact")}
                    </div>
                  )}
                </Col>
              </Row>
            </div>

            {/* Fuel */}
            <div className="dvc">
              <i
                className="las la-times"
                style={{ position: "absolute", zIndex: 9999, border: "1px solid #ddd", borderRadius: "19px", padding: "6px" }}
                onClick={() => { setSlideState("slide-state"); setActiveChart(null); setChartPSN(null); }}
              />
              <Row>
                <h5 style={{ textAlign: "center" }}>{translate("Fuel Tank Diagram")}</h5>
                <Col xs={12}>
                  {FuelTankData && Object.keys(FuelTankData).length > 0 ? (
                    <div style={{ backgroundColor: "#fff", padding: "0", margin: "8" }}>
                      <HighchartsReact highcharts={Highcharts} options={FuelTankData} />
                    </div>
                  ) : (
                    <div style={{ backgroundColor: "#fff", padding: "16px", margin: "8px", textAlign: "center" }}>
                      {translate("No data to display for fuel tank graph")}
                    </div>
                  )}
                </Col>
              </Row>
            </div>

            {/* Temp */}
            <div className="dvc">
              <i
                className="las la-times"
                style={{ position: "absolute", zIndex: 9999, border: "1px solid #ddd", borderRadius: "19px", padding: "6px" }}
                onClick={() => { setSlideState("slide-state"); setActiveChart(null); setChartPSN(null); }}
              />
              <Row>
                <Col xs={12}>
                  <h5 style={{ textAlign: "center" }}>{translate("Temperature Diagram")}</h5>
                  {TemperaturetData && Object.keys(TemperaturetData).length > 0 ? (
                    <div style={{ backgroundColor: "#fff", padding: "0", margin: "8" }}>
                      <HighchartsReact highcharts={Highcharts} options={TemperaturetData} />
                    </div>
                  ) : (
                    <div style={{ backgroundColor: "#fff", padding: "16px", margin: "8px", textAlign: "center" }}>
                      {translate("No data to display for temperature graph")}
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          </Slider>
        </div>
      </Row>
    </>
  );
};

export default Map;
